// react
import { useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';
import useResponsiveChartHeight from '@/features/energyxplorer/hooks/useResponsiveChartHeight';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';
import useChartHighlightParams from '../../hooks/useChartHighlightParams';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/no-data-to-display';
import { DateTime } from 'luxon';

const getWeekNumber = (date) => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  const firstWeekDay = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - firstWeekDay + 3);
  const firstThursdayOfYear = target.valueOf();

  return (
    1 +
    Math.round((firstThursday - firstThursdayOfYear) / (7 * 24 * 3600 * 1000))
  );
};

const hasDSTTransition = (timestamps, timeZone = 'Europe/Copenhagen') => {
  for (let i = 1; i < timestamps.length; i++) {
    const prev = DateTime.fromMillis(timestamps[i - 1], { zone: timeZone });
    const curr = DateTime.fromMillis(timestamps[i], { zone: timeZone });
    if (prev.offset !== curr.offset) return { hasDST: true, index: i };
  }
  return { hasDST: false };
};

const getYear = (date) => new Date(date).getFullYear();

const formatTimestamp = (timestamp, aggregate) => {
  const date = new Date(timestamp);

  switch (aggregate) {
    case 'hour':
      return date.toLocaleString('da-DK', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    case 'day':
      return date.toLocaleDateString('da-DK', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    case 'week': {
      const weekNumber = getWeekNumber(date);
      const year = getYear(date);
      return `uge ${weekNumber} ${year}`;
    }
    case 'month':
      return date.toLocaleDateString('da-DK', { month: 'short' });
    case 'year':
      return date.toLocaleDateString('da-DK', { year: 'numeric' });
    default:
      return date.toLocaleString('da-DK');
  }
};

const normalizeByIndex = (timestamps) => timestamps.map((_, index) => index);

const EnergyAnalysisComparisonSeriesChart = ({
  energyConsumptionData,
  dataSeriesAggregate,
  isLoading,
  node,
  viewMode,
  aggregate,
  comparisonEnergyConsumptionData,
  comparisonItems,
  querySkipped = false,
}) => {
  const chartComponentRef = useRef(null);
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const theme = useTheme();
  const chartHeight = useResponsiveChartHeight();
  const { unit } = useEnergyGroupUnit();

  const { highlightEnabled, plotBands, onChartSelection } =
    useChartHighlightParams();

  // Imperative loading overlay
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
    if (isLoading && !querySkipped) {
      chart.showLoading(`${t('common.loading_data')}`);
      chart.hideNoData();
    } else chart.hideLoading();
  }, [isLoading, querySkipped, t]);

  const themeOptions = useMemo(
    () => (theme.palette.mode === 'dark' ? darkTheme : lightTheme),
    [theme.palette.mode],
  );

  // map comparison data by nodeId
  const comparisonDataMap = useMemo(() => {
    const map = new Map();
    (comparisonEnergyConsumptionData || []).forEach((item) => {
      map.set(item?.EnergySummary?.Id, item?.EnergyData || []);
    });
    return map;
  }, [comparisonEnergyConsumptionData]);

  // Build main/global grid & label lookup
  const mainGrid = useMemo(() => {
    const main = energyConsumptionData?.EnergyData || [];
    const globalTimestamps = Array.from(
      new Set(main.map((d) => Date.parse(d.timestamp))),
    ).sort((a, b) => a - b);

    const normalizedGlobalPeriods = normalizeByIndex(globalTimestamps);

    const timestampLookup = new Map();
    globalTimestamps.forEach((ts, i) => {
      timestampLookup.set(normalizedGlobalPeriods[i], {
        actualTimestamp: ts,
        formattedDate: formatTimestamp(ts, aggregate),
      });
    });

    return { globalTimestamps, normalizedGlobalPeriods, timestampLookup };
  }, [energyConsumptionData, aggregate]);

  // Build series in a memo (no state/effect)
  const chartSeries = useMemo(() => {
    const { globalTimestamps, normalizedGlobalPeriods } = mainGrid;

    if (!globalTimestamps?.length) {
      return {
        lang: {
          noData: querySkipped ? t('common.invalid_filter_combination') : ' ',
        },
        noData: {
          style: {
            fontWeight: 'normal',
            color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
          },
        },
        loading: {
          labelStyle: {
            fontWeight: 'normal',
            color: theme.palette.mode === 'dark' ? 'white' : 'black',
          },
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0)',
          },
          showDuration: 150,
          hideDuration: 150,
        },
        title: { text: '' },
        chart: {
          backgroundColor: themeOptions.chart.backgroundColor,
        },
        series: [],
      };
    }

    const valueKey = dataSeriesAggregate?.value;
    if (!valueKey) return [];

    const mainEnergy = energyConsumptionData?.EnergyData || [];

    // Index main points for O(1) lookup
    const mainByTs = new Map();
    mainEnergy.forEach((d) => mainByTs.set(Date.parse(d.timestamp), d));

    const series = [];

    // MAIN SERIES
    series.push({
      name: `${node?.name} (Valgte målepunkt)`,
      type: viewMode === 'line' ? 'line' : 'column',
      data: globalTimestamps.map((ts, index) => {
        const point = mainByTs.get(ts);
        return {
          x: normalizedGlobalPeriods[index],
          y: point?.[valueKey] ?? null,
          actualTimestamp: ts,
        };
      }),
    });

    // BACKWARDS-ALIGNED COMPARISON SERIES
    const N = normalizedGlobalPeriods.length;

    (comparisonItems || [])
      .filter((item) => item?.node)
      .forEach((item) => {
        const energyData =
          comparisonDataMap.get(item.node.energyTreeNodeId) || [];
        if (!energyData.length) return;

        // timestamps for THIS comparison item
        const itemTimestamps = Array.from(
          new Set(energyData.map((d) => Date.parse(d.timestamp))),
        ).sort((a, b) => a - b);

        const M = itemTimestamps.length;
        if (!M) return;

        const startOffset = Math.max(0, N - M);

        // Index comparison points
        const byTs = new Map();
        energyData.forEach((d) => byTs.set(Date.parse(d.timestamp), d));

        const data = itemTimestamps.map((ts, i) => {
          const point = byTs.get(ts);
          return {
            x: startOffset + i,
            y: point?.[valueKey] ?? null,
            actualTimestamp: ts,
          };
        });

        series.push({
          name: `${item.node.name} (Sammenligning)`,
          type: viewMode === 'line' ? 'line' : 'column',
          data,
          connectNulls: false,
        });
      });

    return series;
  }, [
    mainGrid,
    dataSeriesAggregate?.value,
    energyConsumptionData?.EnergyData,
    node?.name,
    viewMode,
    comparisonItems,
    querySkipped,
    t,
    theme.palette.mode,
    themeOptions.chart.backgroundColor,
    comparisonDataMap,
  ]);

  // DST marker only from main (keep it simple)
  const dstInfo = useMemo(() => {
    const { globalTimestamps } = mainGrid;
    if (!globalTimestamps?.length)
      return { showDSTMarker: false, dstIndex: null };

    const { hasDST, index } = hasDSTTransition(globalTimestamps);
    const showDSTMarker = viewMode === 'line' && hasDST;
    return { showDSTMarker, dstIndex: index ?? null };
  }, [mainGrid, viewMode]);

  // Final options as a memo (drop-in replacement for state)
  const chartOptions = useMemo(() => {
    const { globalTimestamps, timestampLookup } = mainGrid;

    if (!globalTimestamps?.length) {
      return {
        lang: {
          noData: querySkipped ? t('common.invalid_filter_combination') : ' ',
        },
        noData: {
          style: {
            fontWeight: 'normal',
            color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
          },
        },
        loading: {
          labelStyle: {
            fontWeight: 'normal',
            color: theme.palette.mode === 'dark' ? 'white' : 'black',
          },
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0)',
          },
          showDuration: 150,
          hideDuration: 150,
        },
        title: { text: '' },
        chart: {
          backgroundColor: themeOptions.chart.backgroundColor,
        },
        series: [],
      };
    }

    const { showDSTMarker, dstIndex } = dstInfo;

    return {
      lang: {
        noData: `${querySkipped ? t('common.invalid_filter_combination') : ' '}`,
      },
      noData: {
        style: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
        },
      },
      loading: {
        labelStyle: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        style: {
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        showDuration: 150,
        hideDuration: 150,
      },
      chart: {
        type: viewMode === 'line' ? 'line' : 'column',
        style: { cursor: highlightEnabled ? 'crosshair' : 'default' },
        backgroundColor: themeOptions.chart.backgroundColor,
        events: {
          selection: onChartSelection,
          load() {
            const chart = this;

            // Ensure we don't attach multiple listeners (React rerenders / remounts)
            if (chart.__mouseleaveHandler) return;

            chart.__mouseleaveHandler = () => {
              chart.tooltip?.hide(0);
              chart.pointer?.reset?.(true); // clears hover state
              chart.xAxis?.forEach((a) => a.hideCrosshair?.());
              chart.yAxis?.forEach((a) => a.hideCrosshair?.());
            };

            chart.container.addEventListener(
              'mouseleave',
              chart.__mouseleaveHandler,
            );
          },
          destroy() {
            const chart = this;
            if (chart.__mouseleaveHandler) {
              chart.container.removeEventListener(
                'mouseleave',
                chart.__mouseleaveHandler,
              );
              chart.__mouseleaveHandler = null;
            }
          },
        },
        height: chartHeight,
        zooming: { type: 'x', mouseWheel: { enabled: false } },
        spacingLeft: 50,
        spacingRight: 50,
      },
      credits: { enabled: false },
      time: { useUTC: true },
      title: { text: '' },

      xAxis: {
        type: 'linear', // IMPORTANT: keep linear
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        lineColor: themeOptions.xAxis.lineColor,
        plotBands,
        plotLines:
          showDSTMarker && dstIndex != null
            ? [
                {
                  color: 'red',
                  dashStyle: 'shortdash',
                  width: 2,
                  value: dstIndex,
                  zIndex: 5,
                  label: {
                    text: '⏰ DST skift',
                    verticalAlign: 'top',
                    y: -10,
                  },
                },
              ]
            : [],
        labels: {
          useHTML: true,
          formatter: function () {
            const idx = this.value;
            const originalDateObj = timestampLookup.get(idx);
            return originalDateObj
              ? `<div style="text-align:center;">
                   <span style="font-size:12px;">${originalDateObj.formattedDate}</span>
                 </div>`
              : '';
          },
          style: {
            color: themeOptions.xAxis.labels.style.color,
            whiteSpace: 'normal',
            width: '30px',
            textAlign: 'center',
          },
        },
      },

      yAxis: {
        min: 0,
        title: { text: `Energiforbrug (${unit})` },
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
      },

      legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },

      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
        formatter: function () {
          let tooltip = ``;
          (this.points || []).forEach((point) => {
            if (point?.y == null) return;
            const actualTime = formatTimestamp(
              point.point.actualTimestamp,
              aggregate,
            );
            tooltip += `
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${point.series.color};margin-right:5px;"></span>
              ${point.series.name}: <b>${formatNumber(point.y)} ${unit}</b>
              <span style="font-size:12px;color:gray;">(${actualTime})</span><br/>
            `;
          });
          return tooltip || '';
        },
      },

      plotOptions: {
        line: { marker: { enabled: false } },
        column: { grouping: true, borderColor: 'transparent' },
        series: { connectNulls: false },
      },

      series: chartSeries,
    };
  }, [
    aggregate,
    chartHeight,
    chartSeries,
    dstInfo,
    highlightEnabled,
    mainGrid,
    onChartSelection,
    plotBands,
    querySkipped,
    t,
    theme.palette.mode,
    themeOptions.chart.backgroundColor,
    themeOptions.legend.itemStyle.color,
    themeOptions.tooltip.backgroundColor,
    themeOptions.tooltip.borderColor,
    themeOptions.xAxis.labels.style.color,
    themeOptions.xAxis.lineColor,
    themeOptions.yAxis.labels.style.color,
    unit,
    viewMode,
  ]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartComponentRef}
    />
  );
};

export default EnergyAnalysisComparisonSeriesChart;
